import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import axios, { AxiosError } from 'axios';

import { CONSTANTS } from 'src/shared/constants';
import { useLocation } from 'react-router-dom';
import { useUserStore } from 'src/shared/stores/useUserStore';

type BugsnagClient = typeof Bugsnag | null;
const BugsnagContext = createContext<BugsnagClient>(null);
export const useBugsnag = () => useContext(BugsnagContext);

export function BugsnagProvider({ children }: { children: React.ReactNode }) {
  const apiKey = CONSTANTS.BUGSNAG_API_KEY || '';
  const releaseStage = CONSTANTS.APP_ENV;
  const appVersion = '1.0.0';

  const shouldInit = Boolean(apiKey && releaseStage);

  const [client, setClient] = useState<BugsnagClient>(null);
  const [ErrorBoundary, setErrorBoundary] =
    useState<React.ComponentType<any> | null>(null);

  const metaRef = useRef({
    userId: '',
    email: '',
    tenantId: '',
    path: '',
  });

  const { userId, email, tenantId } = useUserStore();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!shouldInit || client) return;

    Bugsnag.start({
      apiKey,
      releaseStage,
      appVersion,
      plugins: [new BugsnagPluginReact()],
      onError(event) {
        // console.log('###OPA ERROR ####', event);
        const errorClass = event.errors?.[0]?.errorClass?.toLowerCase() ?? '';
        const errorMessage =
          event.errors?.[0]?.errorMessage?.toLowerCase() ?? '';
        const originalError = event.originalError?.toLowerCase() || '';

        const skipMatchMessages = [
          'ResizeObserver loop limit exceeded'.toLowerCase(),
          'ResizeObserver loop completed with undelivered notifications.'.toLowerCase(),
        ];

        let shouldSkip = false;

        skipMatchMessages.forEach((message) => {
          if (
            errorClass === message ||
            errorMessage.includes(message) ||
            originalError === message
          ) {
            shouldSkip = true;
          }
        });

        if (shouldSkip) {
          return false;
        }

        const err = event.originalError;

        if (axios.isAxiosError(err)) {
          const axErr = err as AxiosError<any>;

          const method = axErr.config?.method?.toUpperCase() ?? 'HTTP';
          event.context = `${method} ${axErr.config?.url}`;

          event.addMetadata('axios', {
            url: axErr.config?.url,
            baseURL: axErr.config?.baseURL,
            fullUrl: `${axErr.config?.baseURL}${axErr.config?.url}`,
            method: axErr.config?.method,
            status: axErr.response?.status,
            statusText: axErr.response?.statusText,
            requestData: axErr.config?.data,
            responseData: axErr.response?.data,
            headers: axErr.response?.headers,
            message: axErr.message,
          });

          /* (Opcional) ignorar 401 não desejados
          if (axErr.response?.status === 401) return false;
          */
        }
      },
    });

    setClient(Bugsnag);

    const plugin = Bugsnag.getPlugin('react') as any;
    if (plugin?.createErrorBoundary) {
      const Boundary = plugin.createErrorBoundary(React, {
        FallbackComponent: () => (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <h2>
              Houve um problema ao exibir o site.
              <br />
              Se o erro persistir, contate o administrador.
            </h2>
          </div>
        ),
      });
      setErrorBoundary(() => Boundary);
    }
  }, [shouldInit, client, apiKey, releaseStage, appVersion]);

  useEffect(() => {
    if (!client) return;

    if (metaRef.current.userId !== userId || metaRef.current.email !== email) {
      client.setUser(
        userId || undefined,
        email || undefined,
        /* nome (opcional) */ undefined,
      );
      metaRef.current.userId = userId;
      metaRef.current.email = email;
    }

    if (metaRef.current.tenantId !== tenantId) {
      client.addMetadata('tenant', { id: tenantId });
      metaRef.current.tenantId = tenantId;
    }

    if (metaRef.current.path !== pathname) {
      client.addMetadata('route', { path: pathname });
      metaRef.current.path = pathname;
    }
  }, [client, userId, email, tenantId, pathname]);

  return (
    <BugsnagContext.Provider value={client}>
      {ErrorBoundary ? <ErrorBoundary>{children}</ErrorBoundary> : children}
    </BugsnagContext.Provider>
  );
}
